import { TestGroupGate } from "@/components/gate/TestGroupGate";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useExternalConnectionsData } from "@/data/use-external-connections-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloud } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { noop } from "lodash";
import { toast } from "sonner";

type ConnectSourceDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const ConnectSourceDialog: React.FC<ConnectSourceDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const groupUuid = useGroupUuid();
  const { initiateConnection, isInitiating } =
    useExternalConnectionsData(groupUuid);

  const handleConnect = async (provider: "GOOGLE_DRIVE" | "SHAREPOINT") => {
    try {
      await initiateConnection(provider);
      // User will be redirected to OAuth flow
    } catch (error) {
      const detail = error instanceof Error ? error.message : "Unknown error";
      toast.error(
        `Failed to connect to ${getProviderName(provider)}: ${detail}`,
      );
      console.error("Failed to initiate connection:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect External Source</DialogTitle>
          <DialogDescription>
            Connect an external data source to sync files into your knowledge
            base.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Button
            variant="outline"
            className="w-full justify-start gap-3"
            onClick={() => handleConnect("GOOGLE_DRIVE")}
            disabled={isInitiating}
          >
            <FontAwesomeIcon icon={faCloud} className="text-lg text-blue-500" />
            <span>Google Drive</span>
          </Button>
          <TestGroupGate>
            <Button
              variant="outline"
              className="w-full justify-start gap-3"
              onClick={() => handleConnect("SHAREPOINT")}
              disabled={isInitiating}
            >
              <FontAwesomeIcon
                icon={faCloud}
                className="text-lg text-emerald-600"
              />
              <span>SharePoint</span>
            </Button>
          </TestGroupGate>
          <Button
            variant="outline"
            className="w-full justify-start gap-3"
            onClick={noop}
            disabled={isInitiating}
          >
            <FontAwesomeIcon icon={faCloud} className="text-lg text-blue-600" />
            <span>OneDrive (Coming Soon)</span>
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start gap-3"
            onClick={noop}
            disabled={isInitiating}
          >
            <FontAwesomeIcon icon={faCloud} className="text-lg text-blue-400" />
            <span>Dropbox (Coming Soon)</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function getProviderName(
  provider: "GOOGLE_DRIVE" | "SHAREPOINT" | "ONEDRIVE" | "DROPBOX",
): string {
  switch (provider) {
    case "GOOGLE_DRIVE":
      return "Google Drive";
    case "SHAREPOINT":
      return "SharePoint";
    case "ONEDRIVE":
      return "OneDrive";
    case "DROPBOX":
      return "Dropbox";
  }
}
